import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import './LiquidImage.css';

type Props = {
  src: string;
  alt?: string;
  className?: string;
  filterId?: string;
};

/**
 * SVG turbulence + displacement-map filter. On hover, GSAP tweens the
 * displacement scale up and the turbulence frequency for a wavy liquid
 * surface effect on top of any image.
 */
export function LiquidImage({ src, alt = '', className = '', filterId }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const dispRef = useRef<SVGFEDisplacementMapElement>(null);
  const turbRef = useRef<SVGFETurbulenceElement>(null);
  const idRef = useRef(filterId || `liquid-${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const enter = () => {
      if (dispRef.current) {
        gsap.fromTo(
          dispRef.current,
          { attr: { scale: 0 } },
          { attr: { scale: 40 }, duration: 0.9, ease: 'power3.out' }
        );
        gsap.to(dispRef.current, { attr: { scale: 8 }, duration: 1.6, delay: 0.9, ease: 'power3.out' });
      }
      if (turbRef.current) {
        gsap.fromTo(
          turbRef.current,
          { attr: { baseFrequency: '0.005 0.01' } },
          { attr: { baseFrequency: '0.03 0.06' }, duration: 1.4, ease: 'power2.out' }
        );
      }
    };

    const leave = () => {
      if (dispRef.current) {
        gsap.to(dispRef.current, { attr: { scale: 0 }, duration: 0.8, ease: 'power3.inOut' });
      }
      if (turbRef.current) {
        gsap.to(turbRef.current, {
          attr: { baseFrequency: '0.005 0.01' },
          duration: 1,
          ease: 'power2.inOut',
        });
      }
    };

    el.addEventListener('mouseenter', enter);
    el.addEventListener('mouseleave', leave);
    return () => {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mouseleave', leave);
    };
  }, []);

  return (
    <div ref={ref} className={`liquid-image ${className}`}>
      <svg className="liquid-image__filter" width="0" height="0">
        <filter id={idRef.current}>
          <feTurbulence
            ref={turbRef}
            type="fractalNoise"
            baseFrequency="0.005 0.01"
            numOctaves={2}
            seed={4}
          />
          <feDisplacementMap ref={dispRef} in="SourceGraphic" scale={0} />
        </filter>
      </svg>
      <img src={src} alt={alt} loading="lazy" style={{ filter: `url(#${idRef.current})` }} />
    </div>
  );
}
